import { getErrorMessage } from "@captions-web/dev-logger";
import { Icon } from "@captions-web/icons";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { t } from "i18next";
import { Portal } from "radix-ui";
import { Fragment, useState } from "react";
import { toast } from "sonner";

import { GlobalActionButtons } from "~/components/global-action-buttons";
import { KoalaScript } from "~/components/koala-script";
import { MirageLogoSmall } from "~/components/mirage-logo";
import { MobileBlocker } from "~/components/mobile-blocker";
import { AsyncActionButton, Button, LinkButton } from "~/components/primitives/button";
import { DropdownMenu } from "~/components/primitives/dropdown-menu";
import { ScrollArea } from "~/components/primitives/scroll-area";
import { Spinner } from "~/components/primitives/spinner";
import { textTruncateStyles } from "~/components/primitives/text-truncate";
import { TrackPageView } from "~/components/track-page-view";
import { SessionEvents } from "~/lib/analytics/defs/SessionEvents";
import { TeamEvents } from "~/lib/analytics/defs/TeamEvents";
import { useDynamicConfig, useFeatureFlag } from "~/lib/feature-flags/hooks";
import { useOnChangeEffect } from "~/lib/hooks/react-helpers";
import { useInfiniteQueryTriggerInView } from "~/lib/hooks/use-infinite-query-trigger-in-view";
import { useIsMobileBrowser } from "~/lib/hooks/use-is-mobile-browser";
import { getAuthStore, useAuthStore } from "~/modules/auth/auth-store";
import { canUserModifyRoles } from "~/modules/auth/permissions";
import { CreditsPopover } from "~/modules/credits/components/credits-popover";
import { ProjectListItem } from "~/modules/project/components/project-list-item";
import { listProjectsQuery } from "~/modules/project/network/queries";
import { InviteDialog } from "~/modules/teams/components/invite-dialog";
import { listUserOrganizationsQuery } from "~/modules/teams/network/queries";
import { ListOrganizationItem } from "~/modules/teams/network/schema";
import { getIntercomStore } from "~/stores/intercom";
import { getLayoutStore, useLayoutStore } from "~/stores/layout";
import { css, cva } from "~/styled-system/css";

import { ForceAcceptTermsDialog } from "../modules/onboarding/components/terms-dialog";
import { EventStream } from "../modules/tweaks/components/event-stream";

export const Route = createFileRoute("/_app")({
  component: RouteComponent,
  async beforeLoad({ context: { rootStart } }) {
    const profileResponse = await getAuthStore().ensureProfile();

    if (!profileResponse) {
      throw redirect({ to: "/" });
    }

    if (!profileResponse.wasPreviouslyLoggedIn) {
      // we only want to track the initial load latency on the first load of the app
      SessionEvents.start.track({
        initial_load_latency_ms: Math.ceil(performance.now() - rootStart),
      });
    }

    // call intercom.update() on every page navigation
    getIntercomStore().update();

    return { user: profileResponse.user, org: profileResponse.currentOrg };
  },
});

function RouteComponent() {
  const { org } = Route.useRouteContext();
  const sidebarCollapsed = useLayoutStore((s) => s.sidebarCollapsed);
  const isMobile = useIsMobileBrowser();

  // Show mobile blocker for authenticated routes on mobile
  if (isMobile) {
    return <MobileBlocker />;
  }

  return (
    <div key={org.id} className={rootStyles}>
      <TrackPageView />
      <HeavyLoadToast />
      <KoalaScript />
      <ForceAcceptTermsDialog />
      <div
        className={sideBarAnimationStyles({ collapsed: sidebarCollapsed })}
        // note: the `inert` attribute requires a workaround until React 19
        {...{ inert: sidebarCollapsed ? "" : undefined }}
      >
        <Sidebar />
      </div>

      <main className={mainStyles}>
        <Outlet />

        <GlobalActionButtons />
      </main>

      <EventStream />
    </div>
  );
}

function HeavyLoadToast() {
  const { value } = useDynamicConfig("mirage_studio_auto_toasts");

  const showHeavyLoadToast = value?.show_heavy_load_toast;

  useOnChangeEffect(showHeavyLoadToast, (showHeavyLoadToast) => {
    if (showHeavyLoadToast) {
      toast.warning(t("mirage-studio:heavy-load-toast.title"), {
        id: "heavy-load-toast",
        position: "top-right",
        duration: Infinity,
        closeButton: true,
        description: t("mirage-studio:heavy-load-toast.description"),
      });
    } else {
      toast.dismiss("heavy-load-toast");
    }
  });

  return null;
}

function Sidebar() {
  const layout = useChildMatches({
    select: (matches) => {
      if (matches.find((m) => m.routeId === "/_app/settings")) {
        return { type: "settings" as const };
      }

      return { type: "projects" as const };
    },
    structuralSharing: true,
  });

  const navigate = useNavigate();

  const onLogout = async () => {
    await getAuthStore().logout("logout_clicked");
    await navigate({ to: "/" });
  };

  if (layout.type === "settings") {
    return (
      <aside className={sidebarStyles({ type: "settings" })}>
        <div className={settingsBackItemStyles}>
          <LinkButton
            to="/home"
            variant="secondary"
            iconPosition="only"
            size="md"
            icon={<Icon name="left-arrow-small" />}
          />
          <p>{t("mirage-studio:sidebar.settings.title")}</p>
          <Button
            variant="quarternary"
            iconPosition="only"
            icon={<Icon name="position-left" />}
            onClick={() => getLayoutStore().toggleSidebar()}
            className={css({ marginLeft: "auto" })}
            tooltip={{
              content: t("mirage-studio:sidebar.collapse-sidebar"),
            }}
          />
        </div>
        <Link to="/settings/team" className={sidebarLinkItemStyles}>
          <Icon name="teams" />
          <p>{t("mirage-studio:sidebar.settings.team")}</p>
        </Link>
        <Link to="/settings/profile" className={sidebarLinkItemStyles}>
          <Icon name="person" />
          <p>{t("mirage-studio:sidebar.settings.profile")}</p>
        </Link>
        <Link to="/settings/billing" className={sidebarLinkItemStyles}>
          <Icon name="credit-card" />
          <p>{t("mirage-studio:sidebar.settings.billing")}</p>
        </Link>
        <AsyncActionButton
          variant="secondary"
          size="md"
          onClick={onLogout}
          className={css({ marginTop: "24px" })}
        >
          {t("mirage-studio:sidebar.settings.logout")}
        </AsyncActionButton>
      </aside>
    );
  }

  return <ProjectsSidebarList />;
}

function ProjectsSidebarList() {
  const { org } = Route.useRouteContext();
  const navigate = useNavigate();
  const { enabled: enableActorLibrary } = useFeatureFlag("studio_enable_actor_library");

  const user = useAuthStore((s) => s.user);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const { data: organizationsData } = listUserOrganizationsQuery.useQuery();

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading } =
    listProjectsQuery.useInfiniteQuery({
      params: {},
    });

  const loadMoreRef = useInfiniteQueryTriggerInView({ hasNextPage, fetchNextPage });

  const [pendingOrgSwitch, setPendingOrgSwitch] = useState<ListOrganizationItem>();
  const handleSwitchOrganization = async (newOrg: ListOrganizationItem) => {
    if (newOrg.id === org.id) {
      return;
    }

    try {
      setPendingOrgSwitch(newOrg);
      await getAuthStore().switchOrganization(newOrg.id);
      await navigate({ to: "/home" });
      toast.success(t("mirage-studio:sidebar.organization-switched", { team: newOrg.name }));
      TeamEvents.switch_status.track({
        status: "success",
      });
    } catch (error) {
      toast.error(t("mirage-studio:sidebar.organization-switch-error", { team: newOrg.name }));
      TeamEvents.switch_status.track({
        status: "error",
        error_message: getErrorMessage(error),
      });
    } finally {
      setPendingOrgSwitch(undefined);
    }
  };

  return (
    <Fragment>
      {pendingOrgSwitch && <OrgSwitchingOverlay org={pendingOrgSwitch} />}
      <aside className={sidebarStyles({ type: "projects" })}>
        <InviteDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          permissionsMode={canUserModifyRoles(user) ? "admin" : "member"}
        />
        <div className={sideBarTopStyles}>
          <header className={sideBarHeaderStyles}>
            <Link to="/home">
              <MirageLogoSmall isInteractive />
            </Link>
            <Button
              variant="quarternary"
              iconPosition="only"
              icon={<Icon name="position-left" />}
              onClick={() => getLayoutStore().toggleSidebar()}
              tooltip={{
                content: t("mirage-studio:sidebar.collapse-sidebar"),
              }}
            />
          </header>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className={userDropdownTriggerStyles}>
                <p
                  className={css(
                    {
                      textStyle: "body-1-subtle",
                      color: "$gray.200",
                    },
                    textTruncateStyles.raw({ truncated: "1-line" })
                  )}
                >
                  {org.name}
                </p>

                <Icon
                  name="chevron-down"
                  size={16}
                  className={css({
                    color: "$gray.500",
                    flexShrink: 0,
                  })}
                />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start" sideOffset={5}>
              <Link to="/settings/team">
                <DropdownMenu.Item icon={<Icon name="filtering" />}>
                  {t("mirage-studio:sidebar.settings-dropdown.settings")}
                </DropdownMenu.Item>
              </Link>
              <DropdownMenu.Item
                icon={<Icon name="teams" />}
                onClick={() => setInviteDialogOpen(true)}
              >
                {t("mirage-studio:sidebar.settings-dropdown.teams")}
              </DropdownMenu.Item>
              {organizationsData?.organizations && organizationsData.organizations.length > 1 && (
                <DropdownMenu.Sub>
                  <DropdownMenu.SubTrigger icon={<Icon name="reorder" />}>
                    {t("mirage-studio:sidebar.settings-dropdown.switch-teams")}
                  </DropdownMenu.SubTrigger>
                  <DropdownMenu.SubContent>
                    <DropdownMenu.RadioGroup value={org.id}>
                      {organizationsData.organizations.map((organization) => (
                        <DropdownMenu.RadioItem
                          key={organization.id}
                          onClick={() => handleSwitchOrganization(organization)}
                          value={organization.id}
                        >
                          {organization.name}
                        </DropdownMenu.RadioItem>
                      ))}
                    </DropdownMenu.RadioGroup>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          <LinkButton to="/home" size="md" variant="elevated" icon={<Icon name="add" />}>
            {t("mirage-studio:sidebar.new-project")}
          </LinkButton>
          {enableActorLibrary && (
            <Link to="/actors" className={sidebarLinkItemStyles}>
              <Icon name="person" />
              {t("mirage-studio:sidebar.actor-library")}
            </Link>
          )}
        </div>
        <ScrollArea className={projectListStyles} scrollbarVariant="default">
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              paddingTop: "16px",
            })}
          >
            {data?.pages
              .flatMap((page) => page.projects.page)
              .map((project) => {
                return <ProjectListItem key={project.id} project={project} />;
              })}
            {/* only render loadmore element if done with initial fetch */}
            {!isLoading && <div ref={loadMoreRef} className={css({ height: "1px" })} />}
            {(isFetchingNextPage || isLoading) && (
              <div className={loadingMoreSpinnerStyles}>
                <Spinner size={16} />
              </div>
            )}
          </div>
        </ScrollArea>
        <footer className={footerStyles}>
          <CreditsPopover />
        </footer>
      </aside>
    </Fragment>
  );
}

function OrgSwitchingOverlay({ org }: { org: ListOrganizationItem }) {
  return (
    <Portal.Root>
      <div className={orgSwitchingOverlayStyles}>
        <Spinner size={32} />
        <h2 className={css({ textStyle: "heading-4" })}>
          {t("mirage-studio:sidebar.organization-switching", { team: org.name })}
        </h2>
      </div>
    </Portal.Root>
  );
}

const rootStyles = css({
  display: "flex",
  minHeight: "dvh",
  padding: "token(spacing.bleedLayoutPadding)",
  paddingLeft: 0,
  background: "$gray.900",
  color: "$gray.200",
});

const easeOutQuint = "cubic-bezier(0.22, 1, 0.36, 1)";
const sideBarAnimationStyles = cva({
  base: {
    overflow: "hidden",
    transition: ["width", "padding", "margin", "opacity"]
      .map((property) => `${property} 500ms ${easeOutQuint}`)
      .join(", "),
    width: 280,
    marginLeft: 0,
    "@media (max-width: 768px)": {
      position: "absolute",
      height: "100%",
      backgroundColor: "$gray.900",
      zIndex: "$sidebarOverlay",
    },
  },
  variants: {
    collapsed: {
      true: { width: 0, marginLeft: 16, opacity: 0 },
    },
  },
});

const sidebarStyles = cva({
  base: {
    display: "flex",
    flexDirection: "column",

    width: "280px",
    paddingX: "16px",
    maxHeight: "calc(100dvh - 32px)",
    overflow: "hidden",
    height: "100%",
  },

  variants: {
    type: {
      projects: { gap: "0" },
      settings: { gap: "2px" },
    },
  },
});

const mainStyles = css({
  flex: 1,
  borderRadius: "24px",
  background: "$gray.950",
  "@media (max-width: 768px)": {
    marginLeft: 16,
  },
});

const userDropdownTriggerStyles = css({
  flexGrow: 0,
  alignSelf: "start",

  position: "relative",

  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px",

  textAlign: "left",

  cursor: "pointer",
  outline: "none",
  borderRadius: "8px",

  "&:hover, &[data-state=open]": {
    background: "$gray.800",
  },

  "&:focus-visible:after": {
    content: '""',
    position: "absolute",
    inset: -3,
    border: "1.5px solid transparent",
    mask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    background: "$gray.800",
    borderRadius: "11px",
  },
});

const settingsBackItemStyles = css({
  textStyle: "body-1-intense",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginY: "16px",
});

const sidebarLinkItemStyles = css({
  textStyle: "body-3-intense",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 8px",

  borderRadius: "6px",
  color: "$gray.200",

  "& > svg": {
    color: "$gray.400",
  },

  "&:hover": {
    background: "$surface.highlight",
  },

  "&[data-status=active]": {
    background: "$surface.highlight",
  },
});

const sideBarTopStyles = css({
  position: "sticky",
  top: 0,

  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "16px 0",

  background: "$gray.900",
  borderBottom: "$thin",
});

const sideBarHeaderStyles = css({
  display: "flex",
  gap: 8,
  justifyContent: "space-between",
  alignItems: "center",
});

const projectListStyles = css({
  flex: 1,
});

const loadingMoreSpinnerStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px",
});

const footerStyles = css({
  position: "sticky",
  bottom: 0,

  marginTop: "auto",
  paddingTop: "16px",
  paddingBottom: "8px",

  background: "$gray.900",
});

const orgSwitchingOverlayStyles = css({
  position: "absolute",
  inset: 0,
  layerStyle: "blur-md",
  zIndex: "$dialog",

  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
});
